import { brevoTemplates } from "@/components/apprenticeship/candidate/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useUser } from "@/providers/user";
import { ApprenticeshipRegistrationType } from "@/types/apprenticeship/apprentice";
import { Availability, BookedSlot, EventType, User } from "@/types/lms/booking";
import { ActionWrapper } from "@/utils/wretch";
import { fr } from "date-fns/locale";
import { CalendarDays, Check, ChevronLeft, ChevronRight, ChevronsUpDown, Laptop, Loader2, LoaderCircle, Phone, Timer } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { DayModifiers, SelectSingleEventHandler } from "react-day-picker";
import { useFetcher } from "react-router-dom";
import { toast } from "sonner";
import { ScrollArea } from "../scroll-area";

export function BookEventSlot({
    registration,
    eventTypeId,
    hostEmail,
    participants,
    onEventSlotBooked,
    eventSlotPayloadFn
}: {
    registration: ApprenticeshipRegistrationType;
    eventTypeId: string;
    hostEmail?: string;
    participants: { first_name: string; last_name: string; email: string; phone_number: string }[];
    onEventSlotBooked?: (data: BookedSlot) => void;
    eventSlotPayloadFn?: () => Promise<{ sf_opportunity_id?: number | null; walter_session_id?: number | null }>;
}) {
    const { has_any_group } = useUser();
    const is_admin = has_any_group(["app_apprenticeship_admin"]);
    const eventFetcher = useFetcher<EventType>();
    const calendarFetcher = useFetcher<string[]>();
    const availibilityFetcher = useFetcher<Availability>();
    const bookFetcher = useFetcher<ActionWrapper<BookedSlot>>();
    const [selectedMonth, setSelectedMonth] = useState<DateTime>(DateTime.now());
    const [selectedDate, setSelectedDate] = useState<DateTime | undefined>(undefined);
    const [state, setState] = useState<"appointment" | "personal_info" | "confirmed">("appointment");
    const [selectedHour, setSelectedHour] = useState<string | null>(null);
    const [selectedHost, setSelectedHost] = useState<User>();
    const [timezone, setTimezone] = useState("Europe/Paris");

    const DEFAULT_DURATION = 30;
    const DEFAULT_BOOKING_DEADLINE = 43830;

    useEffect(() => {
        eventFetcher.load(`/booking/${eventTypeId}`);
    }, [eventTypeId]);

    useEffect(() => {
        calendarFetcher.load(`/booking/${eventTypeId}/calendar?year=${selectedMonth.year}&month=${selectedMonth.month}${selectedHost ? "&host=" + selectedHost.id : ""}`);
    }, [eventFetcher.data, selectedMonth, selectedHost]);

    useEffect(() => {
        if (selectedDate) {
            availibilityFetcher.load(
                `/booking/${eventTypeId}/availability?year=${selectedDate.year}&month=${selectedDate.month}&day=${selectedDate.day}${selectedHost ? "&host=" + selectedHost.id : ""}`
            );
        }
    }, [eventFetcher.data, selectedDate, selectedHost]);

    useEffect(() => {
        if (eventFetcher.data?.hosts) {
            if (hostEmail) {
                setSelectedHost(eventFetcher.data?.hosts.filter(h => h.email == hostEmail)[0] || null);
            }
        }
    }, [eventFetcher.data?.hosts]);

    useEffect(() => {
        if (bookFetcher.data) {
            if (bookFetcher.data.success) {
                setState("confirmed");
                if (onEventSlotBooked) {
                    onEventSlotBooked(bookFetcher.data.content);
                }
                toast.success("Le rendez-vous a bien été créé");
            } else {
                setState("appointment");
                toast.error("Une erreur est survenue lors de la prise de rendez-vous");
            }
        }
    }, [bookFetcher.data]);

    const handleSelect: SelectSingleEventHandler = (_day, selected) => {
        const selectedDay = DateTime.fromJSDate(selected);
        setSelectedDate(selectedDay);
    };

    const calendarDays = useMemo<DayModifiers>(() => {
        if (!eventFetcher.data) return { available: [] };
        if (!calendarFetcher.data) return { available: [] };
        return {
            available: [
                ...calendarFetcher.data
                    .filter(cal => {
                        return DateTime.fromISO(cal) < DateTime.now().plus({ minutes: eventFetcher.data?.booking_deadline ?? DEFAULT_BOOKING_DEADLINE });
                    })
                    .map(date => {
                        return DateTime.fromISO(date).toJSDate();
                    })
            ]
        };
    }, [calendarFetcher.data]);

    const slots = useMemo(() => {
        if (!availibilityFetcher.data) return undefined;
        return Object.values(availibilityFetcher.data)
            .flat()
            .map(slot => {
                return slot.start_time;
            })
            .filter((value, index, self) => self.indexOf(value) === index)
            .sort((a, b) => {
                return a.localeCompare(b);
            });
    }, [availibilityFetcher.data]);

    const randomSelectedAvailability = useMemo(() => {
        if (!eventFetcher.data || !availibilityFetcher.data) return undefined;
        if (!selectedHour) return undefined;

        const selectedAvailabilities = Object.entries(availibilityFetcher.data).filter(([_, value]) => value.find(slot => slot.start_time === selectedHour));
        const randomSelectedAvailability = selectedAvailabilities[Math.floor(Math.random() * selectedAvailabilities.length)];

        if (!randomSelectedAvailability) return null;

        return {
            host: eventFetcher.data?.hosts.find(host => host.id.toString() === randomSelectedAvailability[0]),
            slot: randomSelectedAvailability[1].find(slot => slot.start_time === selectedHour)
        };
    }, [eventFetcher.data, availibilityFetcher.data, selectedHour]);

    if (eventFetcher.state != "idle")
        return (
            <div className="flex min-h-[300px] items-center justify-center">
                <Loader2 className="animate-spin" />
            </div>
        );

    if (!eventFetcher.data)
        return (
            <div className="flex min-h-[300px] items-center justify-center">
                <p>
                    Aucun événement n'a été trouvé pour la configuration n°{eventTypeId}.<br />
                    Veuillez contacter l'équipe technique pour plus d'informations.
                </p>
            </div>
        );

    if (!eventFetcher.data.hosts)
        return (
            <div className="flex min-h-[300px] items-center justify-center">
                <p>
                    Aucun intervenant n'a été associé à l'événement <b>{eventFetcher.data.title}</b>
                    <br />
                    Veuillez contacter l'équipe technique pour plus d'informations.
                </p>
            </div>
        );

    if (hostEmail && eventFetcher.data.hosts.filter(h => h.email == hostEmail).length == 0)
        return (
            <div className="flex min-h-[300px] items-center justify-center">
                <p>
                    Aucun intervenant n'a été trouvé pour l'adresse email <b>{hostEmail || "unknown"}</b>.<br />
                    Veuillez contacter l'équipe technique pour plus d'informations.
                </p>
            </div>
        );

    return (
        <div className="grid gap-5">
            {state === "appointment" && (
                <Card>
                    <CardContent className="flex h-[425px] flex-col pt-6">
                        <div className="flex h-full flex-col lg:flex-row">
                            <Information description={eventFetcher.data.description} timezone={timezone} setTimezone={setTimezone}>
                                <div className="flex items-center space-x-2">
                                    <div className="max-h-14 min-h-14 min-w-14 max-w-14 rounded-full shadow">
                                        <img className="h-full w-full rounded-full object-cover" src="https://walter-resources.s3.eu-west-3.amazonaws.com/static/default-img/walter-default-logo.jpg" />
                                    </div>
                                    <div className="flex flex-col">
                                        <p className="text-base font-semibold leading-6 text-black">{eventFetcher.data.title}</p>
                                        <p className="text-sm font-medium leading-4">
                                            {eventFetcher.data.show_host_identity && eventFetcher.data?.hosts.length > 0 && hostEmail && selectedHost
                                                ? `${selectedHost.first_name} ${selectedHost.last_name}`
                                                : !hostEmail
                                                  ? eventFetcher.data?.hosts.map(host => `${host.first_name} ${host.last_name}`).join(", ")
                                                  : "Walter Learning"}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-2.5 flex gap-2">
                                    <Timer size={24} />
                                    <p className="font-medium text-black">{eventFetcher.data.duration} min</p>
                                </div>
                                {eventFetcher.data.platform === "phone" ? (
                                    <div className="mt-2 flex gap-2">
                                        <Phone size={24} />
                                        <p className="font-medium text-black">Appel téléphonique</p>
                                    </div>
                                ) : (
                                    <div className="mt-2 flex gap-2">
                                        <Laptop size={24} />
                                        <p className="font-medium text-black">Visioconférence</p>
                                    </div>
                                )}
                            </Information>

                            <div className="border-wild-sand-200 ml-5 border-r-[1px] lg:ml-auto" />

                            <div className="flex w-full max-w-full flex-col items-stretch justify-between space-x-0 space-y-5 sm:flex-row sm:items-start sm:space-y-0 md:mx-auto md:max-w-[460px] md:space-x-10 lg:justify-normal">
                                <div className="flex w-full flex-col">
                                    {(eventFetcher.data.allow_host_selection || is_admin) && eventFetcher.data.hosts.length > 1 && (
                                        <div className="flex items-center">
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button variant="outline" role="combobox" className="mb-2.5 w-full justify-between">
                                                        {selectedHost ? `${selectedHost.first_name} ${selectedHost.last_name}` : "Sélectionner un intervenant"}
                                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                                    <Command>
                                                        <CommandInput placeholder="Sélectionner un intervenant" />
                                                        <CommandList>
                                                            <CommandEmpty>Aucun intervenant trouvé</CommandEmpty>
                                                            <CommandGroup>
                                                                {eventFetcher.data.hosts.map(host => (
                                                                    <CommandItem
                                                                        key={host.id}
                                                                        value={host.id.toString()}
                                                                        onSelect={currentValue => {
                                                                            setSelectedHost(eventFetcher.data?.hosts.filter(h => h.id.toString() == currentValue)[0]);
                                                                        }}
                                                                    >
                                                                        <Check className={cn("mr-2 h-4 w-4", selectedHost?.id === host.id ? "opacity-100" : "opacity-0")} />
                                                                        {host.first_name} {host.last_name}
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                    )}
                                    <div className="flex justify-center">
                                        <Calendar
                                            showOutsideDays={false}
                                            disabled={{
                                                before: DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate(),
                                                after: DateTime.now()
                                                    .plus({ minutes: eventFetcher.data.booking_deadline ?? DEFAULT_BOOKING_DEADLINE })
                                                    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                                                    .toJSDate()
                                            }}
                                            mode="single"
                                            selected={selectedDate?.toJSDate()}
                                            onSelect={handleSelect}
                                            locale={fr}
                                            modifiers={calendarDays}
                                            classNames={{
                                                nav_button: cn(buttonVariants({ variant: "outline" }), "h-9 w-9 bg-transparent p-0 opacity-50 hover:opacity-100 rounded-full p-2.5"),
                                                day: cn(buttonVariants({ variant: "ghost" }), "hover:!bg-blue-200 h-9 w-9 !rounded-full m-auto"),
                                                cell: "h-10 w-10 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-full first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full focus-within:relative focus-within:z-20",
                                                head_cell: "text-muted-foreground rounded-full w-10 font-normal text-[0.8rem]"
                                            }}
                                            modifiersClassNames={{
                                                today: cn(buttonVariants({ variant: "ghost" })),
                                                selected: "bg-blue-700 text-primary-foreground",
                                                available:
                                                    "bg-blue-500 text-primary-foreground after:absolute after:z-10 after:rounded-full after:bg-primary-foreground after:w-1 after:h-1 after:left-1/2 after:-translate-x-1/2 after:bottom-2.5 after:translate-y-1/2"
                                            }}
                                            onMonthChange={month => {
                                                setSelectedMonth(DateTime.fromJSDate(month).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }));
                                                setSelectedDate(undefined);
                                            }}
                                            defaultMonth={selectedMonth.toJSDate()}
                                        />
                                    </div>
                                </div>
                                <div className="flex h-full min-w-40 items-center sm:max-w-40">
                                    {!selectedDate ? (
                                        <p className="text-center text-sm text-slate-500">Veuillez Select a date dans le calendrier pour voir les disponibilités</p>
                                    ) : availibilityFetcher.state != "idle" ? (
                                        <div className="flex h-full w-full items-center justify-center">
                                            <LoaderCircle className="h-6 w-6 animate-spin" />
                                        </div>
                                    ) : slots && slots.length > 0 ? (
                                        <ScrollArea className="mx-auto h-80 min-w-40">
                                            <div>
                                                {slots.map(slot => {
                                                    const hour = DateTime.fromISO(slot).setZone(timezone).toFormat("HH:mm");

                                                    return (
                                                        <div className="flex flex-col space-y-1.5 p-[1px]" key={hour}>
                                                            <div className="flex gap-1">
                                                                <p
                                                                    className={cn(
                                                                        "border-wild-sand-500 box-border flex w-full flex-1 cursor-pointer items-center justify-center rounded border-[1px] py-2.5 font-medium transition hover:border-black hover:outline hover:outline-1 hover:outline-black"
                                                                    )}
                                                                    onClick={() => {
                                                                        setSelectedHour(slot);
                                                                    }}
                                                                >
                                                                    {hour}
                                                                </p>

                                                                <Button
                                                                    size="icon"
                                                                    variant="outline"
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setState("personal_info");
                                                                    }}
                                                                    className={cn("flex h-auto w-0 overflow-hidden transition-all duration-150", selectedHour === slot ? "flex-1" : "invisible w-0")}
                                                                >
                                                                    <ChevronRight strokeWidth={1} />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </ScrollArea>
                                    ) : (
                                        slots !== undefined && <p className="text-center text-sm leading-5 text-black">Aucun créneau n'est disponible le {selectedDate.toLocaleString()}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {state === "personal_info" && randomSelectedAvailability && (
                <Card>
                    <CardContent className="flex flex-col pt-6">
                        <div className="mx-auto max-w-xl">
                            <Button
                                size="icon"
                                className="mb-2.5"
                                variant="ghost"
                                type="button"
                                onClick={() => {
                                    setState("appointment");
                                }}
                            >
                                <ChevronLeft strokeWidth={1} />
                            </Button>
                            <div className="flex flex-col items-center">
                                <Information description={eventFetcher.data.description} className="lg:ml-0 lg:max-w-full">
                                    <div className="flex items-center space-x-2">
                                        <div className="flex flex-col">
                                            <p className="text-lg font-semibold text-black">
                                                {eventFetcher.data.allow_host_selection || hostEmail
                                                    ? `${randomSelectedAvailability?.host?.first_name} ${randomSelectedAvailability?.host?.last_name}`
                                                    : "Walter Learning"}
                                            </p>
                                            <p className="font-medium leading-5">{eventFetcher.data.title}</p>
                                        </div>
                                    </div>
                                    <div className="mt-2.5 flex gap-2">
                                        <Timer size={24} />
                                        <p className="font-medium text-black">{eventFetcher.data.duration} min</p>
                                    </div>
                                    {eventFetcher.data.platform === "phone" ? (
                                        <div className="mt-2 flex gap-2">
                                            <Phone size={24} />
                                            <p className="font-medium text-black">Appel téléphonique</p>
                                        </div>
                                    ) : (
                                        <div className="mt-2 flex gap-2">
                                            <Laptop size={24} />
                                            <p className="font-medium text-black">Visioconférence</p>
                                        </div>
                                    )}
                                    <div className="mt-2 flex gap-2">
                                        <CalendarDays size={24} />
                                        <div className="flex flex-col">
                                            <p className="font-medium text-black">
                                                {DateTime.fromISO(selectedHour as string)
                                                    .setZone(timezone)
                                                    .toLocaleString(DateTime.DATETIME_HUGE, {
                                                        locale: "fr"
                                                    })
                                                    .replace(":", "h")}
                                            </p>
                                            {timezone !== "Europe/Paris" && (
                                                <p className="text-xs font-medium text-black/60">
                                                    {DateTime.fromISO(selectedHour as string)
                                                        .setZone("Europe/Paris")
                                                        .toLocaleString(DateTime.DATETIME_HUGE, {
                                                            locale: "fr"
                                                        })
                                                        .replace(":", "h")}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </Information>

                                <p className="mt-4 text-center text-xs italic">Une confirmation ainsi qu'un lien de connexion seront envoyés à {participants.map(p => p.email).join(", ")}</p>

                                <Separator className="bg-wild-sand-200 !my-2" />
                                <div className="flex max-w-full flex-col">
                                    <Button
                                        type="button"
                                        className="mt-5 w-fit"
                                        disabled={bookFetcher.state != "idle"}
                                        onClick={async () => {
                                            if (!eventFetcher.data) return;
                                            if (!randomSelectedAvailability?.slot?.availability_id) return;
                                            bookFetcher.submit(
                                                {
                                                    ...(eventSlotPayloadFn ? await eventSlotPayloadFn() : {}),
                                                    event_type: eventFetcher.data.id.toString(),
                                                    start_time: selectedHour,
                                                    end_time: DateTime.fromISO(selectedHour as string)
                                                        .plus({ minutes: eventFetcher.data.duration ?? DEFAULT_DURATION })
                                                        .toISO(),
                                                    availability: randomSelectedAvailability?.slot?.availability_id,
                                                    participants: participants,
                                                    template_id:
                                                        brevoTemplates.find(bt => bt.product_code === registration.sf_product?.product_code && bt.event_id.toString() === eventTypeId.toString())?.id ??
                                                        null
                                                },
                                                { method: "post", action: `/booking/book/`, encType: "application/json" }
                                            );
                                        }}
                                    >
                                        {bookFetcher.state != "idle" ? "Réservation en cours..." : "Prendre rendez-vous"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {state === "confirmed" && randomSelectedAvailability && (
                <Card>
                    <CardContent className="flex flex-col pt-6">
                        <div className="mx-auto flex max-w-xl">
                            <Button
                                size="icon"
                                className="mb-2.5"
                                variant="ghost"
                                type="button"
                                onClick={() => {
                                    setState("appointment");
                                }}
                            >
                                <ChevronLeft strokeWidth={1} />
                            </Button>
                            <div className="flex flex-col items-center">
                                <Information description={eventFetcher.data.description} className="lg:ml-0 lg:max-w-full" timezone={timezone} setTimezone={setTimezone}>
                                    <div className="flex items-center space-x-2">
                                        <div className="flex flex-col">
                                            <p className="text-lg font-semibold text-black">
                                                {eventFetcher.data.allow_host_selection || hostEmail
                                                    ? `${randomSelectedAvailability?.host?.first_name} ${randomSelectedAvailability?.host?.last_name}`
                                                    : "Walter Learning"}
                                            </p>
                                            <p className="font-medium leading-5">{eventFetcher.data.title}</p>
                                        </div>
                                    </div>
                                    <div className="mt-2.5 flex gap-2">
                                        <Timer size={24} />
                                        <p className="font-medium text-black">{eventFetcher.data.duration} min</p>
                                    </div>
                                    {eventFetcher.data.platform === "phone" ? (
                                        <div className="mt-2 flex gap-2">
                                            <Phone size={24} />
                                            <p className="font-medium text-black">Appel téléphonique</p>
                                        </div>
                                    ) : (
                                        <div className="mt-2 flex gap-2">
                                            <Laptop size={24} />
                                            <p className="font-medium text-black">Visioconférence</p>
                                        </div>
                                    )}
                                    <div className="mt-2 flex gap-2">
                                        <CalendarDays size={24} />
                                        <p className="font-medium text-black">
                                            {DateTime.fromISO(selectedHour as string)
                                                .toLocaleString(DateTime.DATETIME_HUGE, {
                                                    locale: "fr"
                                                })
                                                .replace(":", "h")}
                                        </p>
                                    </div>
                                </Information>
                                <div className="flex max-w-full flex-col justify-center">
                                    <h1 className="p-8 text-center font-semibold text-black">Votre demande de rendez-vous a bien été enregistrée</h1>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

function Information({
    description,
    children,
    className,
    timezone,
    setTimezone
}: {
    description: string;
    children: React.ReactNode;
    className?: string;
    timezone?: string;
    setTimezone?: (timezone: string) => void;
}) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current) {
            ref.current.querySelectorAll("a").forEach(link => {
                link.setAttribute("target", "_blank");
                link.setAttribute("rel", "noopener noreferrer");
            });
        }
    }, [ref.current]);

    return (
        <div className={cn("mb-5 flex max-w-full flex-col lg:mb-0 lg:ml-5 lg:max-w-96", className)} ref={ref}>
            {children}
            <p className="mt-2.5"> {description}</p>

            {timezone && setTimezone && (
                <div className="mt-auto flex w-full flex-col space-y-1.5">
                    <p className="text-md font-medium">Fuseau horaire :</p>
                    <Select value={timezone} onValueChange={setTimezone}>
                        <SelectTrigger className="">
                            <SelectValue placeholder={"Fuseau horaire"} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="0" disabled>
                                Fuseau horaire
                            </SelectItem>
                            <SelectItem value="Europe/Paris">Paris</SelectItem>
                            <SelectItem value="America/Guadeloupe">Guadeloupe</SelectItem>
                            <SelectItem value="America/Martinique">Martinique</SelectItem>
                            <SelectItem value="America/Cayenne">Guyane</SelectItem>
                            <SelectItem value="Indian/Reunion">Reunion</SelectItem>
                            <SelectItem value="Indian/Mayotte">Mayotte</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    );
}
